import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EuroIcon from "@mui/icons-material/Euro";
import PercentIcon from "@mui/icons-material/Percent";
import { LoadingButton } from "@mui/lab";
import { Autocomplete, Box, Chip, Stack, Switch, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import {
	useUpdateAccountingProfileMutation,
	useCreateAccountingProfileMutation,
	DayOfWeek,
	AccountingProfileEntryInput,
} from "@repo/server-api";
import { BillingAccountEntryType, BillingAccountType } from "@repo/server-api/types";
import { parseIso8601Duration, parseIso8601ToTimeValue, timeValueToIso8601 } from "@repo/shared/common";
import { useNotification, TimeRangeInput, TimeRangeValue } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect } from "react";

import NumberTextField from "../fields/NumberTextField";

export default function DialogBillingAccount(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		profile: BillingAccountType | null;
		refreshData: () => void;
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);

	const [billing, setBilling] = useState<BillingAccountType | null>(null);
	const [billingNameError, setBillingNameError] = useState(false);
	const [entriesError, setEntriesError] = useState(false);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const { showNotification } = useNotification();

	const [updateBillingAccount] = useUpdateAccountingProfileMutation({
		variables: {
			id: billing?.id ?? "",
			name: billing?.name ?? "",
			entries: (billing?.entries ?? []) as AccountingProfileEntryInput[],
		},
		onCompleted(data) {
			if (data.updateAccountingProfile.accountingProfile) {
				props.refreshData();
				handleClose();
				showNotification(t("accounting_profiles.updatedAccountingProfile"), "success");
			}
			if (data.updateAccountingProfile.errors) {
				data.updateAccountingProfile.errors.forEach((err) => {
					if ("errors" in err) {
						err.errors.forEach((e) => {
							showNotification(e.errorMessage, "error");
						});
					} else {
						showNotification(err.message, "error");
					}
				});
			}
			setSaveClicked(false);
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [createBillingAccount] = useCreateAccountingProfileMutation({
		variables: {
			name: billing?.name ?? "",
			entries: (billing?.entries as AccountingProfileEntryInput[]) ?? [],
		},
		onCompleted(data) {
			if (data.createAccountingProfile.accountingProfile) {
				props.refreshData();
				handleClose();
				showNotification(t("accounting_profiles.createdAccountingProfile"), "success");
			}
			if (data.createAccountingProfile.errors) {
				data.createAccountingProfile.errors.forEach((err) => {
					if ("errors" in err && Array.isArray(err.errors)) {
						err.errors.forEach((e) => {
							showNotification(e.errorMessage, "error");
						});
					} else {
						showNotification(err.message, "error");
					}
				});
			}
			setSaveClicked(false);
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	useEffect(() => {
		setOpen(props.open);

		if (props.profile) {
			const profile = props.profile;
			profile.entries?.forEach((e: BillingAccountEntryType, index: number) => {
				e.key = index.toString();
			});
			setBilling(profile);
		} else {
			setBilling(null);
		}
		setBillingNameError(false);
	}, [props.open]);

	const handleClose = () => {
		setOpen(false);
		props.setOpen(false);
		setBillingNameError(false);
		setEntriesError(false);
		setSaveClicked(false);
		setBilling(null);
	};

	const validation = () => {
		let valid = true;
		if (!billing?.name) {
			setBillingNameError(true);
			valid = false;
		}
		if (
			!billing?.entries?.length ||
			billing?.entries?.filter((e) => {
				const fromMinutes = parseIso8601Duration(e.period?.from ?? "PT0H0M");
				const toMinutes = parseIso8601Duration(e.period?.to ?? "PT0H0M");
				return (
					(e.surcharge?.value !== undefined && e.surcharge.value <= 0) ||
					fromMinutes >= toMinutes
				);
			}).length
		) {
			setEntriesError(true);
			valid = false;
		}
		return valid;
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		event.preventDefault();
		if (validation()) {
			setSaveClicked(true);
			billing?.entries?.forEach((e) => {
				delete e.key;
			});

			if (billing?.id) {
				updateBillingAccount();
			} else {
				createBillingAccount();
			}
		}
	};

	const columns: GridColDef<BillingAccountEntryType>[] = [
		{
			field: "daysOfWeek",
			headerName: t("days-of-week", { ns: "common" }),
			width: 400,
			sortable: false,
			renderCell(params: GridCellParams) {
				// Sort initial days from database
				const sortDays = (days: DayOfWeek[]) => {
					const dayOrder = [
						DayOfWeek.Monday,
						DayOfWeek.Tuesday,
						DayOfWeek.Wednesday,
						DayOfWeek.Thursday,
						DayOfWeek.Friday,
						DayOfWeek.Saturday,
						DayOfWeek.Sunday,
					];
					return [...days].sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
				};

				const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>(
					sortDays(params.row.daysOfWeek || []),
				);

				const handleChange = (event: any, newValue: DayOfWeek[]) => {
					const sortedDays = sortDays(newValue);
					setSelectedDays(sortedDays);
					setBilling({
						...billing,
						id: billing?.id ?? "",
						isDeleted: billing?.isDeleted ?? false,
						name: billing?.name ?? "",
						entries: (billing?.entries ?? []).map((e) =>
							e.key === params.row.key ? { ...e, daysOfWeek: sortedDays } : e,
						),
						__typename: billing?.__typename ?? "AccountingProfile",
						version: billing?.version ?? 0,
					});
				};

				const dayOptions = [
					DayOfWeek.Monday,
					DayOfWeek.Tuesday,
					DayOfWeek.Wednesday,
					DayOfWeek.Thursday,
					DayOfWeek.Friday,
					DayOfWeek.Saturday,
					DayOfWeek.Sunday,
				];

				return (
					<Autocomplete
						multiple
						value={selectedDays}
						onChange={handleChange}
						options={dayOptions}
						getOptionLabel={(option) => {
							if (option === DayOfWeek.Sunday) {
								return t("day-of-week.sunday-holiday", { ns: "enum" });
							}
							return t(`day-of-week.${option.toLowerCase()}`, { ns: "enum" });
						}}
						renderTags={(value: DayOfWeek[], getTagProps) =>
							value.map((option: DayOfWeek, index: number) => {
								const { key, ...tagProps } = getTagProps({ index });
								return (
									<Chip
										key={key}
										label={
											option === DayOfWeek.Sunday
												? t("day-of-week.sunday-holiday", { ns: "enum" })
												: t(`day-of-week.${option.toLowerCase()}`, { ns: "enum" })
										}
										size="small"
										{...tagProps}
									/>
								);
							})
						}
						renderInput={(params) => (
							<TextField {...params} variant="standard" sx={{ "& fieldset": { border: "none" } }} />
						)}
						sx={{ width: "100%" }}
						disableCloseOnSelect
					/>
				);
			},
		},
		{
			field: "period",
			headerName: t("period"),
			width: 300,
			sortable: false,
			renderCell(params: GridCellParams) {
				const [timeRange, setTimeRange] = useState<TimeRangeValue>({
					from: parseIso8601ToTimeValue(params.row.period.from),
					to: parseIso8601ToTimeValue(params.row.period.to),
				});

				const handleChange = (newRange: TimeRangeValue) => {
					setTimeRange(newRange);
					setBilling({
						...billing,
						id: billing?.id ?? "",
						isDeleted: billing?.isDeleted ?? false,
						name: billing?.name ?? "",
						entries: ((billing?.entries ?? []) as BillingAccountEntryType[])?.map((e) =>
							e.key === params.row.key
								? {
										...e,
										period: {
											from: timeValueToIso8601(newRange.from),
											to: timeValueToIso8601(newRange.to),
										},
									}
								: e,
						),
						__typename: billing?.__typename ?? "AccountingProfile",
						version: billing?.version ?? 0,
					});
					setEntriesError(false);
				};

				return (
					<div style={{ padding: "16px 0" }}>
						<TimeRangeInput value={timeRange} onChange={handleChange} minuteStep={1} />
					</div>
				);
			},
		},
		{
			field: "isPercent",
			headerName: t("is-percentage"),
			width: 120,
			sortable: false,
			renderCell(params: GridCellParams) {
				return (
					<div style={{ display: "flex", alignItems: "center", height: "100%" }}>
						<Switch
							onChange={(event) => {
								setBilling({
									...billing,
									id: billing?.id ?? "",
									isDeleted: billing?.isDeleted ?? false,
									name: billing?.name ?? "",
									entries: ((billing?.entries ?? []) as BillingAccountEntryType[])?.map((e) =>
										e.key === params.row.key
											? {
													...e,
													surcharge: {
														isPercent: event.target.checked,
														value: e.surcharge?.value ?? 0,
													},
												}
											: e,
									),
									__typename: billing?.__typename ?? "AccountingProfile",
									version: billing?.version ?? 0,
								});
							}}
							checked={params.row.surcharge.isPercent ?? false}
						/>
					</div>
				);
			},
		},
		{
			field: "surcharge",
			headerName: t("surcharge"),
			width: 200,
			sortable: false,
			renderCell(params: GridCellParams) {
				return (
					<div style={{ display: "flex", alignItems: "center", height: "100%" }}>
						<NumberTextField
							isLoaded={true}
							setValue={(value?: number | null) => {
								setBilling({
									...billing,
									id: billing?.id ?? "",
									isDeleted: billing?.isDeleted ?? false,
									name: billing?.name ?? "",
									entries: ((billing?.entries ?? []) as BillingAccountEntryType[])?.map((e) =>
										e.key === params.row.key
											? {
													...e,
													surcharge: {
														isPercent: !!e.surcharge?.isPercent,
														value: value,
													},
												}
											: e,
									) as BillingAccountEntryType[],
									__typename: billing?.__typename ?? "AccountingProfile",
									version: billing?.version ?? 0,
								});
							}}
							value={params.row.surcharge.value}
							decimalPlaces={2}
							minValue={0}
							icon={params.row.surcharge?.isPercent ? <PercentIcon /> : <EuroIcon />}
							flat={true}
						/>
					</div>
				);
			},
		},
		{
			field: "actions",
			headerName: t("actions"),
			sortable: false,
			width: 150,
			renderCell: (params: GridCellParams) => {
				const deleteEntry = (name: string) => {
					const entries = [...(billing?.entries ?? [])];
					entries?.splice(
						entries.findIndex((e) => e.name === name),
						1,
					);
					setBilling(billing ? { ...billing, entries: entries } : null);
				};

				return (
					<div style={{ display: "flex", alignItems: "center", height: "100%" }}>
						<Button onClick={() => deleteEntry(params.row.name)}>
							<DeleteIcon />
						</Button>
					</div>
				);
			},
		},
	];

	const addEntry = () => {
		if (billing?.entries?.filter((e) => !e.daysOfWeek || e.daysOfWeek.length === 0).length) return;
		setEntriesError(false);
		const entry: BillingAccountEntryType = {
			key: billing?.entries?.length.toString() ?? "0",
			name: billing?.entries?.length.toString() ?? "0",
			daysOfWeek: [DayOfWeek.Monday],
			period: {
				from: "PT0H0M",
				to: "PT0H0M",
			},
			surcharge: {
				isPercent: false,
				value: 0,
			},
		};
		setBilling({
			...billing,
			id: billing?.id ?? "",
			isDeleted: billing?.isDeleted ?? false,
			name: billing?.name ?? "",
			entries: [...(billing?.entries ?? []), entry],
			__typename: billing?.__typename ?? "AccountingProfile",
			version: billing?.version ?? 0,
		});
	};

	return (
		<Dialog
			open={open}
			PaperProps={{
				component: "form",
				onSubmit: handleSubmit,
			}}
			maxWidth="xl"
			fullWidth={true}
			data-testid="accounting-profile-dialog">
			<DialogTitle>
				{props.profile ? t("edit-accounting-profile") : t("create-accounting-profile")}
			</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ pt: 1 }}>
					<TextField
						onChange={(event) => {
							setBilling({
								...billing,
								id: billing?.id ?? "",
								isDeleted: billing?.isDeleted ?? false,
								entries: billing?.entries ?? [],
								name: event.target.value ?? "",
								__typename: billing?.__typename ?? "AccountingProfile",
								version: billing?.version ?? 0,
							});
							setBillingNameError(false);
						}}
						error={billingNameError}
						helperText={billingNameError ? t("required", { ns: "validation" }) : ""}
						value={billing?.name ?? ""}
						label={t("name")}
						variant="outlined"
						data-testid="accounting-profile-name-input"
					/>
					<Box sx={{ height: "50vh", width: "100%" }} data-testid="accounting-profile-entries-grid">
						<DataGridPremium
							getRowId={(row: BillingAccountEntryType) => (row.key ? row.key + row.name : "")}
							rows={billing?.entries ?? []}
							columns={columns}
							disableRowSelectionOnClick
							className="table"
							hideFooterPagination={true}
							localeText={{
								footerTotalRows: t("pagination.total-rows"),
							}}
							getRowHeight={(params) => {
								return 100;
							}}
						/>
					</Box>
					{entriesError && (
						<Box sx={{ color: "error.main" }} data-testid="accounting-profile-entries-error">
							{t("accounting-profile-entries-required")}
						</Box>
					)}
					<Button
						variant="contained"
						onClick={addEntry}
						startIcon={<AddIcon />}
						data-testid="accounting-profile-add-entry-button">
						{t("add-new-entry")}
					</Button>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					variant="outlined"
					onClick={handleClose}
					data-testid="accounting-profile-cancel-button">
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					type="submit"
					variant="contained"
					className={saveClicked ? "" : "blueButton"}
					data-testid="accounting-profile-save-button">
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
